import { cn } from '../lib/cn.js';

export type MonogramSize = 'sm' | 'md' | 'lg' | 'xl';
export type MonogramAccent = 'brand' | 'warn' | 'neutral';

export interface MonogramProps {
  /** Two-letter code. Longer strings are truncated to two characters. */
  code: string;
  size?: MonogramSize;
  accent?: MonogramAccent;
  /** Accessible name; falls back to the code. */
  label?: string;
  className?: string;
}

const SIZE: Record<MonogramSize, string> = {
  sm: 'h-9 w-9 text-xs-plus',
  md: 'h-10 w-10 text-nav',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-xl',
};

const ACCENT: Record<MonogramAccent, string> = {
  brand: 'bg-brand text-white',
  warn: 'bg-warn/20 text-warn border border-warn/40',
  neutral: 'bg-card text-ink border border-line',
};

/** Square 2-letter mono monogram for non-person entities (DESIGN_SYSTEM section 6.7). */
export function Monogram({ code, size = 'md', accent = 'neutral', label, className }: MonogramProps) {
  const text = code.slice(0, 2).toUpperCase();
  return (
    <span
      role="img"
      aria-label={label ?? code}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-sm font-mono uppercase tracking-wider',
        SIZE[size],
        ACCENT[accent],
        className,
      )}
    >
      {text}
    </span>
  );
}
